import { API_URL } from '../config/apiConfig';

export interface TemplateToVideoRequest {
  templateId: string;
  faceImage: File;
}

export interface TemplateToVideoResponse {
  jobId: string;
}

export interface JobStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  videoUrl?: string;
  error?: string;
}

/**
 * Mock job simulation for offline mode
 */
export function simulateTemplateToVideoJob(
  onProgress: (progress: number) => void,
  onComplete: (jobId: string, videoUrl: string) => void
): () => void {
  const jobId = `mock-template-job-${Date.now()}`;
  let progress = 0;
  const interval = setInterval(() => {
    // Simulate progress: 0 → 15 → 45 → 75 → 100
    if (progress < 15) {
      progress = 15;
    } else if (progress < 45) {
      progress = 45;
    } else if (progress < 75) {
      progress = 75;
    } else {
      progress = 100;
    }

    onProgress(progress);

    if (progress >= 100) {
      clearInterval(interval);
      // Use a mock video URL (reuse existing mock video or create new path)
      const videoUrl = '/mock/video.mp4';
      onComplete(jobId, videoUrl);
    }
  }, 1500); // Update every 1.5 seconds for ~6-9 second total

  return () => clearInterval(interval);
}

/**
 * Start a template-to-video generation job
 */
export async function startTemplateToVideoJob(
  request: TemplateToVideoRequest
): Promise<TemplateToVideoResponse> {
  const API_BASE_URL = API_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_URL is not configured.');
  }

  const formData = new FormData();
  formData.append('templateId', request.templateId);
  formData.append('faceImage', request.faceImage);

  const response = await fetch(`${API_BASE_URL}/v1/video/template-to-video`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to start job' }));
    throw new Error(error.message || 'Failed to start template-to-video job');
  }

  return await response.json();
}

/**
 * Poll job status
 */
export async function pollTemplateJob(jobId: string): Promise<JobStatus> {
  const API_BASE_URL = API_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_URL is not configured.');
  }

  const response = await fetch(`${API_BASE_URL}/v1/video/jobs/${jobId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch job status' }));
    throw new Error(error.message || 'Failed to fetch job status');
  }

  return await response.json();
}

